'use client';

import { useUser } from '@clerk/nextjs';
import { Alert, AlertDescription } from '@web-app/components/ui/Alert/alert';
import { Button } from '@web-app/components/ui/Button/button';
import { Card } from '@web-app/components/ui/Card/card';
import { Skeleton } from '@web-app/components/ui/Skeleton/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@web-app/components/ui/Tabs/tabs';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { cn } from '@web-app/lib/utils';
import { trpc } from '@web-app/utils/trpc';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LuActivity, LuInfo } from 'react-icons/lu';

import { formatGlucoseSources, formatGlucoseValue } from './glucose-display';
import {
  buildGlucoseSummary,
  DailyGlucoseSummary,
  formatGlucoseDateRange,
} from './glucose-summary';
import { resolveGlucoseTimezone } from './glucose-time';
import { GlucosePeriod, GlucoseReading } from './glucose-types';
import { resolveGlucoseView } from './resolve-glucose-view';

const GlucoseChart = dynamic(
  () => import('./GlucoseChart').then((module) => module.GlucoseChart),
  { ssr: false },
);

type GlucoseTabProps = {
  patientId: string;
  isActive: boolean;
};

const PERIOD_TAB_CLASS =
  'relative z-10 h-6 rounded-xl bg-transparent text-foreground shadow-none opacity-60 transition-opacity duration-200 data-[active]:bg-transparent data-[active]:opacity-100 data-[active]:shadow-none';

export function GlucoseTab({ patientId, isActive }: GlucoseTabProps) {
  const { user, isLoaded } = useUser();
  const { trackEvent } = useAnalytics();
  const hasTrackedFirstOpen = useRef(false);
  const shouldLoad =
    isLoaded && Boolean(user?.id) && isActive && Boolean(patientId);
  const history = trpc.glucoseHistory.get.useQuery(
    {
      patientId,
      // Cache-keying only; the server authorizes with its Clerk session.
      viewerId: user?.id ?? '',
    },
    { enabled: shouldLoad, retry: false },
  );

  useEffect(
    function trackFirstGlucoseTabOpen() {
      if (isActive && !hasTrackedFirstOpen.current) {
        hasTrackedFirstOpen.current = true;
        trackEvent(EVENT_NAMES.GLUCOSE_TAB_VIEWED);
      }
    },
    [isActive, trackEvent],
  );

  if (!isActive) return null;

  const view = resolveGlucoseView({
    isLoading: history.isLoading,
    isError: history.isError,
    readingCount: history.data?.readings.length ?? 0,
  });

  return (
    <section
      className="flex h-full min-w-0 flex-col gap-4 overflow-x-hidden overflow-y-auto px-[18px] py-4"
      aria-labelledby="glucose-heading"
    >
      <h2 id="glucose-heading" className="sr-only">
        Glucose trends
      </h2>

      {view === 'loading' && <GlucoseLoadingState />}
      {view === 'error' && (
        <GlucoseErrorState onRetry={() => history.refetch()} />
      )}
      {view === 'empty' && <GlucoseEmptyState />}
      {view === 'data' && history.data && (
        <GlucoseHistoryContent
          readings={history.data.readings}
          patientTimezone={history.data.timezone}
          isHistoricalFallback={history.data.isHistoricalFallback}
        />
      )}
    </section>
  );
}

function GlucoseLoadingState() {
  return (
    <div role="status" aria-live="polite" className="space-y-4">
      <span className="sr-only">Loading glucose history…</span>
      <Skeleton className="h-9 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-20 w-full rounded-[14px]" />
        <Skeleton className="h-20 w-full rounded-[14px]" />
      </div>
      <Skeleton className="h-72 w-full rounded-[14px]" />
    </div>
  );
}

function GlucoseErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card role="alert" className="p-4">
      <p className="font-medium text-foreground">
        Unable to load glucose history.
      </p>
      <p className="mb-3 text-sm text-muted-foreground">
        Please try again. No data was changed.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

function GlucoseEmptyState() {
  return (
    <Card
      role="status"
      aria-live="polite"
      className="rounded-[20px] border-dashed p-4 sm:p-5"
    >
      <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <LuActivity className="size-4 shrink-0" aria-hidden="true" />
        Glucose
      </h3>
      <p className="mt-3 text-sm leading-5 text-muted-foreground">
        No glucose data. The patient may not have connected Apple Health or
        Health Connect, or their CGM app may not be sharing data.
      </p>
      <p className="mt-4 rounded-[14px] bg-muted px-4 py-3 text-sm leading-5 text-muted-foreground">
        Ask them to enable health data sharing in the Berry Street app. Readings
        may take a few hours to appear.
      </p>
    </Card>
  );
}

function GlucoseHistoryContent({
  readings,
  patientTimezone,
  isHistoricalFallback,
}: {
  readings: GlucoseReading[];
  patientTimezone: string | null;
  isHistoricalFallback: boolean;
}) {
  const { timezone, usedUtcFallback } = resolveGlucoseTimezone(patientTimezone);
  const summary = useMemo(
    () => buildGlucoseSummary(readings, timezone),
    [readings, timezone],
  );
  const [period, setPeriod] = useState<GlucosePeriod>('week');
  const [selectedDayKey, setSelectedDayKey] = useState<string>();
  const selectedDay =
    summary.days.find((day) => day.dayKey === selectedDayKey) ??
    summary.days.at(-1);

  if (
    !selectedDay ||
    summary.average === null ||
    summary.minimum === null ||
    summary.maximum === null
  ) {
    return <GlucoseEmptyState />;
  }

  const selectPeriod = (value: string) => {
    if (value === 'day' || value === 'week') setPeriod(value);
  };
  const selectDay = (dayKey: string) => {
    setSelectedDayKey(dayKey);
    setPeriod('day');
  };

  return (
    <Tabs value={period} onValueChange={selectPeriod} className="min-w-0">
      {isHistoricalFallback ? (
        <Alert className="mb-4" variant="info" role="status" aria-live="polite">
          <LuInfo aria-hidden="true" />
          <AlertDescription>
            No readings in the last 7 days. Showing the most recent available
            data.
          </AlertDescription>
        </Alert>
      ) : null}

      <TabsList className="relative isolate grid h-auto w-full grid-cols-2 rounded-2xl p-[3px]">
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-[3px] left-[3px] w-[calc((100%-6px)/2)] rounded-xl bg-background shadow-default-sm transition-transform duration-200 ease-out',
            period === 'week' && 'translate-x-full',
          )}
        />
        <TabsTrigger value="day" aria-label="Day" className={PERIOD_TAB_CLASS}>
          D
        </TabsTrigger>
        <TabsTrigger
          value="week"
          aria-label="Week"
          className={PERIOD_TAB_CLASS}
        >
          W
        </TabsTrigger>
      </TabsList>

      <TabsContent value="week" className="mt-4 min-w-0 space-y-4">
        <SummaryHeader
          average={summary.average}
          minimum={summary.minimum}
          maximum={summary.maximum}
          unit={summary.readings[0]?.unit ?? 'mg/dL'}
        />
        <div>
          <p className="text-sm font-medium text-foreground">
            {formatGlucoseDateRange(summary, timezone)}
          </p>
          <TimezoneLabel
            timezone={timezone}
            usedUtcFallback={usedUtcFallback}
          />
        </div>
        <Card className="min-w-0 px-2 pt-2 shadow-default-xs">
          <GlucoseChart
            period="week"
            readings={summary.readings}
            days={summary.days}
            average={summary.average}
            timezone={timezone}
          />
        </Card>
        <DailySummary
          days={summary.days}
          selectedDayKey={selectedDay.dayKey}
          onSelectDay={selectDay}
        />
      </TabsContent>

      <TabsContent value="day" className="mt-4 min-w-0 space-y-4">
        <SummaryHeader
          average={selectedDay.average}
          minimum={selectedDay.minimum}
          maximum={selectedDay.maximum}
          unit={selectedDay.readings[0]?.unit ?? 'mg/dL'}
        />
        <div>
          <p className="text-sm font-medium text-foreground">
            {selectedDay.dateLabel}
          </p>
          <TimezoneLabel
            timezone={timezone}
            usedUtcFallback={usedUtcFallback}
          />
        </div>
        <Card className="min-w-0 px-2 pt-2 shadow-default-xs">
          <GlucoseChart
            period="day"
            readings={selectedDay.readings}
            days={[selectedDay]}
            average={selectedDay.average}
            timezone={timezone}
          />
        </Card>
        <DailySummary
          days={summary.days}
          selectedDayKey={selectedDay.dayKey}
          onSelectDay={selectDay}
        />
      </TabsContent>
    </Tabs>
  );
}

function SummaryHeader({
  average,
  minimum,
  maximum,
  unit,
}: {
  average: number;
  minimum: number;
  maximum: number;
  unit: string;
}) {
  return (
    <dl className="grid grid-cols-2 gap-3">
      <Card className="p-3">
        <dt className="text-xs text-muted-foreground">Average glucose</dt>
        <dd className="mt-1 text-xl font-semibold text-foreground">
          <span>{formatGlucoseValue(average)}</span>{' '}
          <span className="text-xs font-normal text-muted-foreground">
            {unit}
          </span>
        </dd>
      </Card>
      <Card className="p-3">
        <dt className="text-xs text-muted-foreground">Observed range</dt>
        <dd className="mt-1 text-xl font-semibold text-foreground">
          <span>
            {formatGlucoseValue(minimum)}–{formatGlucoseValue(maximum)}
          </span>{' '}
          <span className="text-xs font-normal text-muted-foreground">
            {unit}
          </span>
        </dd>
      </Card>
    </dl>
  );
}

function TimezoneLabel({
  timezone,
  usedUtcFallback,
}: {
  timezone: string;
  usedUtcFallback: boolean;
}) {
  return (
    <p className="mt-0.5 text-xs text-muted-foreground">
      Times shown in {timezone}
      {usedUtcFallback ? ' (UTC fallback)' : ''}
    </p>
  );
}

function DailySummary({
  days,
  selectedDayKey,
  onSelectDay,
}: {
  days: DailyGlucoseSummary[];
  selectedDayKey: string;
  onSelectDay: (dayKey: string) => void;
}) {
  return (
    <section aria-labelledby="daily-summary-heading" className="space-y-3">
      <h3
        id="daily-summary-heading"
        className="text-base font-semibold text-foreground"
      >
        Daily summary
      </h3>
      {[...days].reverse().map((day) => (
        <Card
          key={day.dayKey}
          className="shadow-default-xs transition-colors hover:bg-muted/50"
        >
          <button
            type="button"
            aria-label={`View ${day.dateLabel} readings`}
            aria-current={day.dayKey === selectedDayKey ? 'date' : undefined}
            className="flex w-full items-center justify-between gap-3 p-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-[current=date]:bg-muted/50"
            onClick={() => onSelectDay(day.dayKey)}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground">
                {day.dateLabel}
              </span>
              <span className="block text-xs text-muted-foreground">
                {formatGlucoseSources(day.readings.map(({ source }) => source))}
              </span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block text-sm font-semibold text-foreground">
                {formatGlucoseValue(day.minimum)}–
                {formatGlucoseValue(day.maximum)}{' '}
                {day.readings[0]?.unit ?? 'mg/dL'}
              </span>
              <span className="block text-xs text-muted-foreground">
                Avg {formatGlucoseValue(day.average)}
              </span>
            </span>
          </button>
        </Card>
      ))}
    </section>
  );
}
